import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import CorpusSearch from "../../components/war-room/CorpusSearch";
import {
  useCaseRelevance, useSetRelevanceReview, useBulkVerifyRelevance,
  type RelevanceClaim, type RelevanceElement, type RelevanceFact, type RelevanceHumanStatus,
} from "../../hooks/useCaseRelevance";

/**
 * Layer 2 — Relevance / materiality ledger. Per claim -> element -> the facts
 * material to it (required_for | supports), two-model read, human review
 * (verify / adjust required<->supports / reject), and a corpus search per fact.
 * Bare elements (no supporting fact) are flagged — those are the soft seams.
 */
function RelevanceLedger() {
  const { caseId } = useParams<{ caseId: string }>();
  const { data: claims, isLoading, error } = useCaseRelevance(caseId);
  const bulk = useBulkVerifyRelevance(caseId);
  const [confirming, setConfirming] = useState(false);

  let supported = 0, total = 0, pending = 0, links = 0;
  for (const c of claims ?? []) {
    supported += c.supported_elements; total += c.total_elements;
    for (const el of c.elements) for (const f of el.facts) { links++; if (f.needs_review) pending++; }
  }

  return (
    <CommandLayout caseId={caseId} variant="ctafinal">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">// layer 2 — relevance</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          Which facts carry each element.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          For every true fact, which required ELEMENT of a live claim does it carry — and how hard.
          <span style={{ color: VC.required_for }}> required-for</span> = necessary;
          <span style={{ color: VC.supports }}> supports</span> = corroborates. Elements with no fact are
          <span style={{ color: BARE }}> bare</span> — the soft seams. You verify; only confirmed links arm the claim.
        </p>

        {(claims?.length ?? 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
            <span>{supported}/{total} elements supported</span>
            <span>·</span><span>{links} material links</span>
            <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: HC.pending }} />{pending} pending</span>
            <GradingGuide />
            {pending > 0 && !confirming && (
              <button type="button" onClick={() => setConfirming(true)}
                className="flex items-center gap-1.5 rounded border border-[#5a8f6b]/50 px-2 py-0.5 normal-case tracking-normal text-bone hover:bg-[#5a8f6b]/20">
                ✓ verify all {pending}
              </button>
            )}
            {confirming && (
              <span className="flex items-center gap-2 normal-case tracking-normal">
                <span className="font-display text-sm text-bone">Verify all {pending} pending links?</span>
                <button type="button" disabled={bulk.isPending}
                  onClick={() => bulk.mutate(undefined, { onSettled: () => setConfirming(false) })}
                  className="rounded border border-[#5a8f6b]/60 bg-[#5a8f6b]/15 px-2 py-0.5 font-display text-sm uppercase tracking-[0.1em] text-bone hover:bg-[#5a8f6b]/30 disabled:opacity-50">
                  {bulk.isPending ? "verifying…" : "yes"}
                </button>
                <button type="button" onClick={() => setConfirming(false)}
                  className="rounded border border-steel-dark/70 px-2 py-0.5 font-display text-sm uppercase tracking-[0.1em] text-bone-mute hover:text-bone">cancel</button>
              </span>
            )}
            <Link to={`/case/${caseId}/authority`} className="ml-auto normal-case tracking-normal text-blue-bright/80 hover:text-blue-bright">→ authority</Link>
            <Link to={`/case/${caseId}/arrows`} className="normal-case tracking-normal text-blue-bright/80 hover:text-blue-bright">→ light the arrows</Link>
          </div>
        )}
      </header>

      <div className="space-y-3">
        {isLoading && <p className="font-display text-base text-bone-mute">// mapping facts to elements…</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && (claims?.length ?? 0) === 0 && (
          <p className="rounded border border-steel-dark bg-iron/70 px-5 py-6 font-display text-base text-bone-mute">
            // no relevance map yet — run the Layer 2 engine (muster-relevance) first
          </p>
        )}
        {(claims ?? []).map((c) => <ClaimCard key={c.claim_id} c={c} />)}
      </div>
    </CommandLayout>
  );
}

const VC: Record<string, string> = { required_for: "#b3503f", supports: "#c9a23a" };
const vColor = (k: string | null | undefined): string => (k && VC[k]) || "#5a6470";
const VL: Record<string, string> = { required_for: "required-for", supports: "supports" };
const vLabel = (k: string | null | undefined): string => (k && (VL[k] ?? k)) || "—";
const HC: Record<string, string> = { pending: "#a78bdb", verified: "#5a8f6b", adjusted: "#c9a23a", rejected: "#6b7480" };
const hColor = (k: string): string => HC[k] ?? "#a78bdb";
const HL: Record<string, string> = { pending: "pending", verified: "verified", adjusted: "adjusted", rejected: "rejected" };
const BARE = "#8a6db0";
const GUIDE: Record<string, string> = {
  required_for: "Necessary to establish this element — without it, the element fails.",
  supports: "Corroborates or strengthens the element, but is not by itself necessary.",
};

function GradingGuide() {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative normal-case tracking-normal">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded border border-steel-dark/70 px-2 py-0.5 text-bone-mute hover:text-bone">ⓘ required vs supports</button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-[22rem] rounded border border-steel-dark bg-charcoal/95 p-3 shadow-lg backdrop-blur-sm">
          {(["required_for", "supports"] as const).map((k) => (
            <div key={k} className="mb-2 flex items-start gap-2">
              <span className="mt-0.5 shrink-0 rounded px-2 py-0.5 font-display text-sm uppercase tracking-[0.1em]" style={{ background: vColor(k), color: "#0c0f12" }}>{vLabel(k)}</span>
              <span className="font-display text-[12px] leading-snug text-bone-dim">{GUIDE[k]}</span>
            </div>
          ))}
          <p className="font-display text-[11px] italic text-bone-mute/50">Analytical guidance — your counsel makes the final call.</p>
        </div>
      )}
    </span>
  );
}

function ClaimCard({ c }: { c: RelevanceClaim }) {
  const [open, setOpen] = useState(false);
  const bare = c.total_elements - c.supported_elements;
  return (
    <div className="overflow-hidden rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <button type="button" onClick={() => setOpen((o) => !o)} className="block w-full text-left hover:bg-iron-2/40">
        <div className="flex items-start gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-light text-bone">{c.claim_name}</p>
            {c.target && <p className="mt-0.5 font-display text-sm text-bone-mute/70">vs {c.target}</p>}
          </div>
          <span className="shrink-0 rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em]"
                style={{ background: bare > 0 ? BARE : "#5a8f6b", color: "#0c0f12" }}>
            {c.supported_elements}/{c.total_elements} supported{bare > 0 ? ` · ${bare} bare` : ""}
          </span>
          <span className="mt-0.5 shrink-0 font-display text-base text-bone-mute">{open ? "▾" : "▸"}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-steel-dark/60 px-5 py-3 space-y-2">
          {c.elements.map((el) => <ElementRow key={el.element_id} el={el} />)}
        </div>
      )}
    </div>
  );
}

function ElementRow({ el }: { el: RelevanceElement }) {
  const [open, setOpen] = useState(false);
  const bare = el.facts.length === 0;
  return (
    <div className="rounded border border-steel-dark/60 bg-black/20">
      <button type="button" onClick={() => !bare && setOpen((o) => !o)} className={`block w-full text-left ${bare ? "" : "hover:bg-iron-2/30"}`}>
        <div className="flex items-start gap-2 border-l-2 px-3 py-2" style={{ borderColor: bare ? BARE : "#3a424c" }}>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[13px] text-bone">{el.element_name}</p>
            {el.must_prove && <p className="mt-0.5 font-display text-[11px] leading-snug text-bone-mute/70">{el.must_prove}</p>}
          </div>
          {bare ? (
            <span className="shrink-0 rounded px-2 py-0.5 font-display text-[11px] uppercase tracking-[0.1em]" style={{ background: BARE, color: "#0c0f12" }}>bare — no fact</span>
          ) : (
            <span className="shrink-0 font-display text-sm text-bone-mute">{el.facts.length} fact{el.facts.length === 1 ? "" : "s"} {open ? "▾" : "▸"}</span>
          )}
        </div>
      </button>
      {open && !bare && (
        <div className="space-y-2 px-3 pb-2.5">
          {el.facts.map((f) => <FactRow key={f.id} f={f} />)}
        </div>
      )}
    </div>
  );
}

function FactRow({ f }: { f: RelevanceFact }) {
  const { caseId } = useParams<{ caseId: string }>();
  const review = useSetRelevanceReview(caseId);
  const [note, setNote] = useState(f.human_note ?? "");
  const [adjusting, setAdjusting] = useState(false);
  const eff = f.human_verdict ?? f.verdict;
  const hs = f.human_status ?? "pending";
  const split = f.review_status === "split";

  const submit = (s: RelevanceHumanStatus, verdict?: string | null) =>
    review.mutate({ id: f.id, status: s, note: note.trim() || undefined, verdict: verdict ?? null });
  const btn = "rounded border px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em] transition-colors disabled:opacity-50";

  return (
    <div className="rounded border bg-black/20 px-3 py-2" style={{ borderColor: `${hColor(hs)}55` }}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em]" style={{ background: vColor(eff), color: "#0c0f12" }}>{vLabel(eff)}</span>
        {split && <span className="rounded px-1.5 py-0.5 font-display text-[11px] uppercase tracking-[0.1em]" style={{ background: "#a78bdb", color: "#0c0f12" }}>split</span>}
        <span className="rounded px-2 py-0.5 font-display text-[11px] uppercase tracking-[0.1em]" style={{ background: hColor(hs), color: "#0c0f12" }}>{HL[hs] ?? hs}</span>
        <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">¶ {f.paragraph_number}</span>
      </div>
      <p className="font-display text-[12.5px] leading-snug text-bone">{f.allegation_text}</p>
      {(f.proposer_verdict || f.judge_verdict) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">
          <span className="flex items-center gap-1">proposer<span className="rounded px-1 py-0.5" style={{ background: vColor(f.proposer_verdict), color: "#0c0f12" }}>{vLabel(f.proposer_verdict)}</span></span>
          <span className="flex items-center gap-1">cross-exam<span className="rounded px-1 py-0.5" style={{ background: vColor(f.judge_verdict), color: "#0c0f12" }}>{vLabel(f.judge_verdict)}</span></span>
        </div>
      )}
      {f.rationale && <p className="mt-1.5 font-display text-[12px] leading-snug text-bone-dim">{f.rationale}</p>}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" disabled={review.isPending} onClick={() => submit("verified")}
          className={`${btn} border-[#5a8f6b]/60 text-bone hover:bg-[#5a8f6b]/20`} style={hs === "verified" ? { background: hColor("verified"), color: "#0c0f12" } : undefined}>✓ verify</button>
        <button type="button" disabled={review.isPending} onClick={() => setAdjusting((a) => !a)}
          className={`${btn} border-[#c9a23a]/60 text-bone hover:bg-[#c9a23a]/20`} style={hs === "adjusted" ? { background: hColor("adjusted"), color: "#0c0f12" } : undefined}>✎ adjust</button>
        <button type="button" disabled={review.isPending} onClick={() => submit("rejected")}
          className={`${btn} border-steel-dark text-bone-mute hover:bg-black/30`} style={hs === "rejected" ? { background: hColor("rejected"), color: "#0c0f12" } : undefined}>✕ not material</button>
        {hs !== "pending" && <button type="button" disabled={review.isPending} onClick={() => submit("pending")} className={`${btn} border-steel-dark/50 text-bone-mute/70 hover:text-bone`}>↺ clear</button>}
        {review.isError && <span className="font-display text-sm text-ember-bright">// {(review.error as Error).message}</span>}
      </div>
      {adjusting && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="font-display text-sm text-bone-mute/70">corrected to:</span>
          {(["required_for", "supports"] as const).map((vv) => (
            <button key={vv} type="button" onClick={() => { submit("adjusted", vv); setAdjusting(false); }}
              className="rounded px-2 py-0.5 font-display text-sm uppercase tracking-[0.1em]" style={{ background: vColor(vv), color: "#0c0f12" }}>{vLabel(vv)}</button>
          ))}
        </div>
      )}
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1}
        placeholder="note — why (calibrates the next run)"
        className="mt-2 w-full resize-y rounded border border-steel-dark/60 bg-black/30 px-2 py-1 font-display text-[12px] text-bone placeholder:text-bone-mute/50" />
      <CorpusSearch seed={f.allegation_text ?? ""} label="⌕ search the corpus for this" />
    </div>
  );
}

export default RelevanceLedger;
